import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Lightning, MagnifyingGlass, Globe, Check, Brain, Database, Camera, Clock, ArrowRight, ChatCircle, Plus, Trash, Pause, Play, CalendarCheck } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP_ICONS = {
  search: MagnifyingGlass,
  browse: Globe,
  links: Globe,
  data: Database,
  screenshot: Camera,
  think: Brain,
  done: Check,
};

export default function AgentHistoryPage() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [newPrompt, setNewPrompt] = useState('');
  const [newFreq, setNewFreq] = useState('once');
  const [creating, setCreating] = useState(false);

  const loadSchedules = async () => {
    try {
      const { data } = await axios.get(`${API}/agent/schedules`);
      setSchedules(data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/agent/tasks`);
        setTasks(data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
    loadSchedules();
  }, []);

  const createSchedule = async () => {
    if (!newPrompt.trim()) return;
    setCreating(true);
    try {
      await axios.post(`${API}/agent/schedules`, { prompt: newPrompt.trim(), frequency: newFreq });
      setNewPrompt('');
      loadSchedules();
    } catch (e) { console.error(e); }
    finally { setCreating(false); }
  };

  const toggleSchedule = async (id) => {
    try { await axios.patch(`${API}/agent/schedules/${id}/toggle`); loadSchedules(); } catch (e) { console.error(e); }
  };

  const deleteSchedule = async (id) => {
    try { await axios.delete(`${API}/agent/schedules/${id}`); loadSchedules(); } catch (e) { console.error(e); }
  };

  return (
    <div className="min-h-screen bg-transparent" data-testid="agent-history-page">
      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="flex items-center gap-3 mb-8">
          <Link to="/app" className="w-8 h-8 rounded-lg flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-all" data-testid="agent-history-back">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-xl font-semibold tracking-tight" style={{ fontFamily: 'Clash Display, sans-serif' }}>Agent Task History</h1>
            <p className="text-sm text-white/40">Past tasks your agent completed</p>
          </div>
        </div>

        {/* Scheduled tasks */}
        <div className="glass-panel rounded-2xl p-5 mb-8" data-testid="schedules-section">
          <div className="flex items-center gap-2 mb-4">
            <CalendarCheck weight="duotone" className="w-4 h-4 text-lime-400" />
            <h2 className="text-sm font-semibold" style={{ fontFamily: 'Clash Display, sans-serif' }}>Scheduled agent tasks</h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <input
              value={newPrompt}
              onChange={(e) => setNewPrompt(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && createSchedule()}
              placeholder="e.g. Check the top AI news headlines and summarize them"
              data-testid="schedule-prompt-input"
              className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/90 placeholder:text-white/30 outline-none focus:border-lime-400/40"
            />
            <select value={newFreq} onChange={(e) => setNewFreq(e.target.value)} data-testid="schedule-frequency-select"
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/80 outline-none [&>option]:bg-gray-900">
              <option value="once">Run once</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
            <button onClick={createSchedule} disabled={creating || !newPrompt.trim()} data-testid="schedule-create-button"
              className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-lime-500 hover:bg-lime-600 text-black text-sm font-semibold transition-colors disabled:opacity-40">
              <Plus weight="bold" className="w-3.5 h-3.5" /> Schedule
            </button>
          </div>
          {schedules.length === 0 ? (
            <p className="text-xs text-white/35" data-testid="schedules-empty">No scheduled tasks yet. The agent will run these automatically and drop results into your conversations.</p>
          ) : (
            <div className="space-y-2">
              {schedules.map((s) => (
                <div key={s.id} className="flex items-center gap-3 rounded-lg bg-white/5 border border-white/10 px-3 py-2" data-testid={`schedule-row-${s.id}`}>
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${s.active ? 'bg-lime-400' : 'bg-white/20'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-white/80 truncate">{s.prompt}</p>
                    <p className="text-[10px] text-white/35 mt-0.5">
                      {s.frequency} · {s.active ? `next run ${new Date(s.next_run_at).toLocaleString()}` : 'paused'}
                      {s.last_run_at ? ` · last ran ${new Date(s.last_run_at).toLocaleString()}` : ''}
                    </p>
                  </div>
                  <button onClick={() => toggleSchedule(s.id)} data-testid={`schedule-toggle-${s.id}`}
                    className="w-6 h-6 rounded flex items-center justify-center text-white/40 hover:text-lime-300 hover:bg-white/10 transition-all">
                    {s.active ? <Pause weight="fill" className="w-3 h-3" /> : <Play weight="fill" className="w-3 h-3" />}
                  </button>
                  <button onClick={() => deleteSchedule(s.id)} data-testid={`schedule-delete-${s.id}`}
                    className="w-6 h-6 rounded flex items-center justify-center text-white/40 hover:text-red-400 hover:bg-white/10 transition-all">
                    <Trash className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {loading && (
          <div className="flex justify-center py-16">
            <div className="w-5 h-5 border-2 border-white/10 border-t-lime-500 rounded-full animate-spin" />
          </div>
        )}

        {!loading && tasks.length === 0 && (
          <div className="text-center py-16 border border-dashed border-white/10 rounded-xl">
            <Lightning weight="duotone" className="w-10 h-10 text-white/60 mx-auto mb-3" />
            <p className="text-sm text-white/50 mb-1">No agent tasks yet</p>
            <p className="text-xs text-white/40">Toggle Agent mode in the chat and give Lindey a complex task</p>
          </div>
        )}

        <div className="space-y-3">
          {tasks.map((task) => {
            const isExpanded = expanded === task.id;
            return (
              <motion.div
                key={task.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-white/10 rounded-xl overflow-hidden hover:border-white/25 transition-colors"
              >
                <button
                  onClick={() => setExpanded(isExpanded ? null : task.id)}
                  className="w-full text-left p-4 flex items-start gap-3"
                  data-testid={`agent-task-${task.id}`}
                >
                  <div className="w-8 h-8 rounded-lg bg-lime-400/10 border border-lime-400/20 flex items-center justify-center shrink-0 mt-0.5">
                    <Lightning weight="duotone" className="w-4 h-4 text-lime-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white/90 truncate">{task.conversation_title}</p>
                    <p className="text-xs text-white/40 mt-0.5 line-clamp-2">{task.content_preview}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-[10px] text-white/40 flex items-center gap-1">
                        <Lightning className="w-3 h-3" /> {task.steps?.length || 0} steps
                      </span>
                      <span className="text-[10px] text-white/40 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {new Date(task.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <Link
                    to="/app"
                    onClick={(e) => e.stopPropagation()}
                    className="shrink-0 text-xs text-lime-400 hover:text-lime-300 flex items-center gap-1"
                  >
                    Open <ArrowRight className="w-3 h-3" />
                  </Link>
                </button>

                {isExpanded && task.steps && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    className="border-t border-white/10 bg-white/5 px-4 py-3"
                  >
                    <p className="text-[10px] uppercase tracking-wide text-white/40 font-medium mb-2">Agent Steps</p>
                    <div className="space-y-1.5">
                      {task.steps.map((step, i) => {
                        const Icon = STEP_ICONS[step.type] || Lightning;
                        return (
                          <div key={i} className="flex items-center gap-2 text-xs">
                            <div className="w-5 h-5 rounded bg-lime-400/15 flex items-center justify-center shrink-0">
                              {step.status === 'completed' ? (
                                <Check weight="bold" className="w-2.5 h-2.5 text-lime-400" />
                              ) : (
                                <Icon className="w-2.5 h-2.5 text-lime-500" />
                              )}
                            </div>
                            <span className="text-white/70 font-mono text-[11px]">{step.step}</span>
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
